import { useState, useRef } from 'react';
import { Globe, RefreshCw, ExternalLink, ArrowLeft, ArrowRight, Home } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { useArtifactPanelStore } from '@/stores/artifactPanelStore.js';

interface BrowserArtifactProps {
  tabId: string;
}

export function BrowserArtifact({ tabId }: BrowserArtifactProps) {
  const { artifacts, updateArtifact } = useArtifactPanelStore();
  const artifact = artifacts[tabId];
  const initialUrl = (artifact?.data as { url?: string })?.url || '';

  const [url, setUrl] = useState(initialUrl);
  const [inputUrl, setInputUrl] = useState(initialUrl);
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const navigateTo = (newUrl: string) => {
    let finalUrl = newUrl;
    // Add https:// if no protocol specified
    if (!/^https?:\/\//i.test(newUrl)) {
      finalUrl = `https://${newUrl}`;
    }
    setUrl(finalUrl);
    setInputUrl(finalUrl);
    setIsLoading(true);

    // Update artifact title
    try {
      const hostname = new URL(finalUrl).hostname;
      updateArtifact(tabId, { title: hostname, data: { url: finalUrl } });
    } catch {
      // Invalid URL, keep current title
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigateTo(inputUrl);
  };

  const handleRefresh = () => {
    if (iframeRef.current) {
      setIsLoading(true);
      iframeRef.current.src = url;
    }
  };

  const handleOpenExternal = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleHome = () => {
    setUrl('');
    setInputUrl('');
    setIsLoading(false);
    updateArtifact(tabId, { title: 'Browser', data: { url: '' } });
  };

  if (!initialUrl && !url) {
    return (
      <div className="flex flex-col h-full bg-background">
        {/* URL Bar */}
        <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border-b border-border/50 bg-muted/30">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleHome}>
            <Home className="h-4 w-4" />
          </Button>
          <form onSubmit={handleSubmit} className="flex-1 flex items-center gap-2">
            <div className="relative flex-1">
              <Globe className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={inputUrl}
                onChange={(e) => setInputUrl(e.target.value)}
                placeholder="Enter a URL..."
                className="pl-8 h-9"
              />
            </div>
            <Button type="submit" size="sm" className="h-9">
              Go
            </Button>
          </form>
        </div>

        {/* Empty state */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto">
              <Globe className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-lg font-medium mb-1">Browse the web</h3>
              <p className="text-sm text-muted-foreground">Enter a URL above to get started</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Browser toolbar */}
      <div className="flex-shrink-0 flex items-center gap-1 px-2 py-1.5 border-b border-border/50 bg-muted/30">
        {/* Navigation buttons */}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => iframeRef.current?.contentWindow?.history.back()}
          title="Go back"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => iframeRef.current?.contentWindow?.history.forward()}
          title="Go forward"
        >
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={handleRefresh}
          title="Refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={handleHome}
          title="Home"
        >
          <Home className="h-3.5 w-3.5" />
        </Button>

        {/* URL bar */}
        <form onSubmit={handleSubmit} className="flex-1 flex items-center gap-1 mx-1">
          <div className="relative flex-1">
            <Globe className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              placeholder="Enter URL..."
              className="pl-7 h-7 text-sm"
            />
          </div>
        </form>

        {/* Open external */}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={handleOpenExternal}
          title="Open in new tab"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* iframe container */}
      <div className="flex-1 relative bg-white">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={url}
          className="w-full h-full border-0"
          onLoad={() => setIsLoading(false)}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          title="Browser"
        />
      </div>
    </div>
  );
}
